
/**
 * Proof approval. AC-3.3.
 *
 * Approve is the primary action. "Request a change" is deliberately secondary
 * and reveals its note field only once chosen, because asking everyone to read
 * past a textarea to find the button they want is friction for the
 * common case.
 */

import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { Button, Callout, Field, inputClass, textareaClass } from '@/components/ui';

export function ProofResponse({
  proofId,
  quoteRef,
  round,
}: {
  proofId: string;
  quoteRef: string;
  round: number;
}) {
  // The key from the link the customer was emailed (R-157).
  const { t: token } = useSearch({ strict: false }) as { t?: string };
  const navigate = useNavigate();
  const router = useRouter();
  const [mode, setMode] = useState<'idle' | 'change'>('idle');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function respond(action: 'approve' | 'request_change') {
    if (action === 'request_change' && note.trim().length < 3) {
      setError('Tell us what to change so the next round gets it right.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/proofs-respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The key from the link. An anonymous quote has no owner, so this is
        // the only thing that says the person clicking is the customer (R-157).
        body: JSON.stringify({
          proof_id: proofId,
          action,
          note: note.trim() || undefined,
          token,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body?.error?.message ?? 'That did not go through.');
        return;
      }
      // Carry the key back, or the page they land on is a 404 (R-157).
      navigate({
        to: '/quotes/$ref',
        params: { ref: quoteRef },
        search: token ? { t: token } : {},
      });
      router.invalidate();
    } catch {
      setError('That did not go through. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-[10px] border border-line bg-panel p-5">
      <h2 className="text-sm font-bold">Your response</h2>

      {error && (
        <div className="mt-4">
          <Callout tone="danger" title={error} />
        </div>
      )}

      {mode === 'idle' ? (
        <div className="mt-4 space-y-3">
          <Button
            onClick={() => respond('approve')}
            size="lg"
            className="w-full"
            disabled={submitting}
          >
            {submitting ? 'Sending' : `Approve round ${round}`}
          </Button>
          <Button
            weight="secondary"
            size="lg"
            className="w-full"
            onClick={() => setMode('change')}
          >
            Request a change
          </Button>
          <p className="text-[13px] text-muted">
            Approving is final for this round and we start production from it.
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <Field
            label="What should change"
            htmlFor="proof-note"
            hint="Be specific: sizes, placement, colours."
          >
            <textarea
              id="proof-note"
              rows={5}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={textareaClass}
            />
          </Field>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => respond('request_change')} disabled={submitting}>
              {submitting ? 'Sending' : 'Send change request'}
            </Button>
            <Button weight="secondary" onClick={() => setMode('idle')}>
              Back
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
